import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifications } from '@mantine/notifications';
import { Modal, Button, ControlledInput, Input } from '@shared/ui';
import { useUpdateColor, useCreateColor, type Color } from '@modules/products';
import './EditColorModal.css';

const HEX_COLOR = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const colorSchema = yup.object({
  name: yup.string().trim().required('Название обязательно'),
  hexCode: yup
    .string()
    .trim()
    .test('hex', 'Укажите цвет в формате #RRGGBB', (value) => !value || HEX_COLOR.test(value)),
});

type ColorForm = {
  name: string;
  hexCode: string;
};

function normalizeHexInput(raw: string) {
  const value = raw.replace(/^#+/, '');
  return value ? `#${value}` : '';
}

function toSwatchHex(value: string) {
  const match = value.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (!match) return null;
  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  return `#${hex}`;
}

interface EditColorModalProps {
  isOpen: boolean;
  onClose: () => void;
  color: Color | null;
  onSuccess?: () => void;
}

export default function EditColorModal({ isOpen, onClose, color, onSuccess }: EditColorModalProps) {
  const updateColor = useUpdateColor();
  const createColor = useCreateColor();
  const isEdit = !!color;
  const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<ColorForm>({
    resolver: yupResolver(colorSchema) as never,
    defaultValues: { name: '', hexCode: '' },
  });
  const hexCode = watch('hexCode') ?? '';
  const swatchHex = toSwatchHex(hexCode);

  useEffect(() => {
    reset({
      name: color?.name ?? '',
      hexCode: color?.hexCode ?? '',
    });
  }, [color, reset]);

  const onSubmit = async (data: ColorForm) => {
    const payload = {
      name: data.name,
      hexCode: data.hexCode || undefined,
    };

    try {
      if (isEdit && color) {
        await updateColor.mutateAsync({
          id: color.id,
          data: payload,
        });
      } else {
        await createColor.mutateAsync(payload);
      }

      notifications.show({
        title: 'Успешно',
        message: `Цвет успешно ${isEdit ? 'обновлен' : 'создан'}`,
        color: 'green',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEdit ? 'обновлении' : 'создании'} цвета`,
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    reset({ name: '', hexCode: '' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} size={400} onClose={handleClose} title={isEdit ? "Редактировать цвет" : "Добавить цвет"} centered>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ControlledInput
            id="color-name"
            label="Название"
            name="name"
            control={control}
            error={errors.name}
          />
          <Input
            label="HEX код"
            value={hexCode}
            error={errors.hexCode?.message}
            onChange={(e) => {
              setValue('hexCode', normalizeHexInput(e.target.value), { shouldValidate: true });
            }}
            placeholder="#FF0000"
            rightSectionWidth={40}
            rightSectionPointerEvents="all"
            rightAddon={(
              <label className="color-hex-swatch">
                <span
                  className="color-hex-swatch__fill"
                  style={swatchHex ? { backgroundColor: swatchHex } : undefined}
                />
                <input
                  type="color"
                  value={swatchHex ?? '#000000'}
                  onChange={(e) => setValue('hexCode', e.target.value, { shouldValidate: true })}
                  aria-label="Выберите цвет"
                />
              </label>
            )}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button type="button" onClick={handleClose} variant='secondary'>
              Отмена
            </Button>
            <Button type="submit" variant="primary" loading={updateColor.isPending || createColor.isPending}>
              Сохранить
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
